'use client'

import { RefObject, useCallback, useEffect, useRef } from 'react'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { markFirstTimeListerEducationAsRead } from 'data/api/mark-first-time-lister-education-as-read'
import { clickEvent } from '_libs/common/event-tracker/events'
import { firstTimeListerEducationPageLeaveEvent } from '@marketplace-web/domain/conversation-safety-data'
import { useTracking } from '@marketplace-web/shared/event-tracker'

const LAST_SLIDE_INDEX = 2

type Props = {
  educationContainerRef: RefObject<HTMLDivElement | null>
}

const getScrollDepth = (element: HTMLDivElement, scrollOffset = 0) => {
  const documentHeight = element.scrollHeight
  const clientHeight = element.clientHeight + scrollOffset

  if (clientHeight <= 0) {
    return 0
  }

  return Math.floor((clientHeight / documentHeight) * 100)
}

const usePhishingFirstTimeListerEducationTracking = ({ educationContainerRef }: Props) => {
  const initialTime = useRef(new Date().getTime())
  const initialPageScrollDepth = useRef(0)
  const maxPageScrollDepth = useRef(0)

  const { track } = useTracking()

  const handleScrollDepthUpdate = useCallback(() => {
    const element = educationContainerRef.current

    if (!element) return

    const initialScrollDepth = getScrollDepth(element)
    const currentScrollDepth = getScrollDepth(element, element.scrollTop)

    initialPageScrollDepth.current = initialScrollDepth

    if (currentScrollDepth > maxPageScrollDepth.current) {
      maxPageScrollDepth.current = currentScrollDepth
    }
  }, [educationContainerRef])

  const resetTrackingData = () => {
    initialTime.current = new Date().getTime()
    maxPageScrollDepth.current = 0
  }

  const trackFirstTimeListerEducationPageLeaveEvent = (
    currentSlideIndex: number,
    nextSlideIndex: number | null,
  ) => {
    handleScrollDepthUpdate()

    const currentTime = new Date().getTime()

    track(
      firstTimeListerEducationPageLeaveEvent({
        pageNumber: currentSlideIndex + 1,
        goingToPageNumber: nextSlideIndex ? nextSlideIndex + 1 : null,
        timeSpentOnPageMs: currentTime - initialTime.current,
        initialPageScrollDepth: initialPageScrollDepth.current,
        pageScrollDepth: maxPageScrollDepth.current,
      }),
    )
  }

  const onSeeTipsClick = () => {
    resetTrackingData()

    track(
      clickEvent({
        target: ClickableElement.FirstTimeListerEducationSeeTips,
        screen: Screen.FirstTimeListerEducation,
      }),
    )
  }

  const onSlideChange = (currentSlideIndex: number, nextSlideIndex: number) => {
    trackFirstTimeListerEducationPageLeaveEvent(currentSlideIndex, nextSlideIndex)

    resetTrackingData()
  }

  const onHelpCenterClick = () => {
    track(
      clickEvent({
        target: ClickableElement.FirstTimeListerEducationShowHelpCenter,
        screen: Screen.FirstTimeListerEducation,
      }),
    )
  }

  const onDialogClose = () => {
    markFirstTimeListerEducationAsRead()

    track(
      clickEvent({
        target: ClickableElement.FirstTimeListerEducationClose,
        screen: Screen.FirstTimeListerEducation,
      }),
    )
    trackFirstTimeListerEducationPageLeaveEvent(LAST_SLIDE_INDEX, null)
  }

  useEffect(() => {
    const element = educationContainerRef.current

    if (!element) return undefined

    element.addEventListener('scroll', handleScrollDepthUpdate)

    return () => {
      element.removeEventListener('scroll', handleScrollDepthUpdate)
    }
  }, [educationContainerRef, handleScrollDepthUpdate])

  return { onSeeTipsClick, onSlideChange, onHelpCenterClick, onDialogClose }
}

export default usePhishingFirstTimeListerEducationTracking
